<template>
  <div class="w-full" data-vp-disclosure data-static-disclosure>
    <button
      type="button"
      :class="buttonClasses"
      :aria-expanded="isOpen"
      :aria-label="props['aria-label']"
      data-vp-disclosure-trigger
      data-static-disclosure-trigger
      @click="toggle"
    >
      <!-- Left side: Icon (optional) + Text -->
      <div class="flex items-center gap-3 min-w-0 flex-1">
        <div v-if="$slots.icon" class="shrink-0">
          <slot name="icon" />
        </div>
        <div class="min-w-0 flex-1 text-left">
          <slot name="header" />
        </div>
        <div v-if="$slots.trailing" class="shrink-0">
          <slot name="trailing" />
        </div>
      </div>
      <!-- Right side: Chevron or arrow-right (design: top-level text-white/60, submenu text-sm text-white/40) -->
      <i
        v-if="chevron === 'arrow-right'"
        class="ri-arrow-right-s-line transition-transform duration-200 flex-shrink-0"
        :class="[
          nested ? 'text-sm text-text-muted' : 'text-text-secondary',
          isOpen ? 'rotate-90' : ''
        ]"
      ></i>
      <svg
        v-else
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        :class="chevronClasses"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>
    
    <div
      class="base-disclosure-panel mt-1 overflow-hidden"
      :class="{ 'base-disclosure-panel--open': isOpen }"
      :inert="!isOpen"
      data-vp-disclosure-panel
      data-static-disclosure-panel
    >
      <div class="base-disclosure-panel-inner min-h-0">
        <slot name="content" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

interface Props {
  open?: boolean
  'aria-label'?: string
  /** 'arrow-right' for sidebar (rotates 90° when open); default chevron down */
  chevron?: 'default' | 'arrow-right'
  /** Nested submenu header (e.g. Amazon, Inquiries): denser, secondary text like design */
  nested?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  chevron: 'default',
  nested: false
})

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

// Internal state for uncontrolled usage
const internalOpen = ref(false)

// Use prop if provided, otherwise use internal state
const isOpen = computed(() => {
  return props.open !== undefined ? props.open : internalOpen.value
})

const buttonClasses = computed(() => {
  // Design: top-level px-3 py-2 rounded-lg text-sm; nested submenu headers px-3 py-1.5 rounded-md text-[13px] text-dark-text-secondary
  const dense = props.nested
  const padding = dense ? 'px-3 py-1.5' : 'px-3 py-2'
  const rounded = dense ? 'rounded-md' : 'rounded-lg'
  const textSize = dense ? 'text-[13px]' : 'text-sm'
  const baseClasses = `group w-full flex items-center justify-between gap-3 ${padding} ${textSize} font-medium ${rounded} transition-colors duration-200 rounded-b-none `
  const textClass = props.nested ? 'text-dark-text-secondary hover:bg-surface-hover' : 'text-dark-text-primary hover:bg-surface-hover'

  if (isOpen.value) {
    const activeText = 'text-dark-text-primary'
    return `${baseClasses} bg-surface-muted ${activeText}`
  }

  return `${baseClasses} ${textClass}`
})

const chevronClasses = computed(() => {
  return [
    'h-4 w-4 shrink-0 text-text-muted',
    isOpen.value ? 'rotate-180' : '',
  ].filter(Boolean).join(' ')
})

const toggle = () => {
  const newValue = !isOpen.value
  if (props.open !== undefined) {
    // Controlled component - emit to parent
    emit('update:open', newValue)
  } else {
    // Uncontrolled component - update internal state
    internalOpen.value = newValue
  }
}
</script>

<style scoped>
/* Smooth height: animate grid row from 0fr → 1fr (content stays mounted for controlled open state). */
.base-disclosure-panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.3s ease-out;
}

@media (prefers-reduced-motion: reduce) {
  .base-disclosure-panel {
    transition: none;
  }
}

.base-disclosure-panel--open {
  grid-template-rows: 1fr;
}

.base-disclosure-panel-inner {
  overflow: hidden;
}
</style>
